import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  alpha,
  useTheme,
  useMediaQuery
} from '@mui/material';
import useAuth from '../hooks/useAuth';
import { APP_NAME } from '../App';

// Using Ailyce's login image
const loginImageUrl = 'https://infanion.lienion.com/Loginimg.png';
const logoUrl = '/logo-gray.svg';

/**
 * Forgot Password Page
 * Allows users to request a password reset
 */
const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const { forgotPassword, loading } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    
    try {
      await forgotPassword(email);
      setSubmitted(true);
    } catch (err) {
      console.error('Forgot password error:', err);
      setError(err.message || 'Failed to send reset instructions. Please try again.');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        backgroundColor: '#f5f5f5',
      }}
    >
      {/* Left section - Brand */}
      {!isMobile && (
        <Box
          sx={{
            flex: '0 0 58.33%', // col-7 (7/12 = 58.33%)
            backgroundImage: `url(${loginImageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div className="login_bg_overlay__PPsaZ"></div>
          <Box sx={{ position: 'relative', textAlign: 'center', color: 'white', px: 6, zIndex: 1 }}>
            {/* Welcome text removed as requested */}
          </Box>
        </Box>
      )}

      {/* Right section - Reset Password form */}
      <Box
        sx={{
          flex: isMobile ? '1' : '0 0 41.67%', // col-5 (5/12 = 41.67%)
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 3, md: 4 },
          backgroundColor: '#fff',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: isMobile ? '100%' : '420px',
            p: 4,
            borderRadius: '4px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            border: 'none',
          }}
        >
          {/* Logo */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <img 
              src={logoUrl} 
              alt="Tremel AI Logo" 
              style={{ height: '80px', marginBottom: '16px' }}
              onError={(e) => {
                e.target.style.display = 'none';
                // Show text fallback if image fails to load
                document.getElementById('reset-logo-text').style.display = 'block';
              }}
            />
            <Typography 
              id="reset-logo-text" 
              variant="h4" 
              component="div" 
              fontWeight="bold" 
              color={theme.palette.primary.main} 
              sx={{ display: 'none' }}
            >
              {APP_NAME}
            </Typography>
          </Box>

          <Typography 
            variant="body1" 
            component="h2" 
            gutterBottom 
            fontWeight="medium"
            sx={{ 
              fontSize: 'var(--font-size-16px)', 
              mb: 3, 
              color: 'var(--color-blue) !important' 
            }}
          >
            Reset password
          </Typography>
          
          {submitted ? (
            <Box sx={{ textAlign: 'center', py: 2, width: '304px' }}>
              <Box 
                sx={{ 
                  mb: 3,
                  p: 2,
                  width: '100%',
                  bgcolor: alpha(theme.palette.success.main, 0.08),
                  color: 'success.dark',
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2" color="inherit">
                  Password reset instructions have been sent to your email.
                </Typography>
              </Box>
              <Typography 
                variant="body2" 
                paragraph
                sx={{ 
                  color: 'var(--color-blue) !important',
                  mb: 3
                }}
              >
                Please check your email for instructions to reset your password.
              </Typography>
              <Button
                fullWidth
                variant="contained"
                onClick={() => navigate('/login')}
                sx={{ 
                  py: 1.2,
                  height: '40px',
                  bgcolor: '#485869', 
                  color: '#ffffff',
                  fontFamily: '"Sofia Pro Regular", "Sofia Pro", sans-serif',
                  fontSize: 'var(--font-size-20px)',
                  lineHeight: 'var(--line-height-30px)',
                  fontWeight: 400,
                  border: '1px solid #485869',
                  borderRadius: '5px',
                  textTransform: 'capitalize',
                  '&:hover': {
                    bgcolor: '#3A4754',
                  },
                  boxShadow: 'none',
                }}
              >
                Return to login
              </Button>
            </Box>
          ) : (
            <>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'var(--color-blue) !important',
                  textAlign: 'center', 
                  mb: 3,
                  width: '304px'
                }}
              >
                Enter your email address and we'll send you instructions to reset your password.
              </Typography>
              
              {error && (
                <Box 
                  sx={{ 
                    mb: 3,
                    p: 2,
                    width: '304px',
                    bgcolor: alpha(theme.palette.error.main, 0.08),
                    color: 'error.dark',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" color="inherit">
                    {error}
                  </Typography>
                </Box>
              )}
              
              {/* Reset Password Form */}
              <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%', mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {/* Email Field */}
                <Box sx={{ mb: 5, width: '304px' }}>
                  <Typography 
                    component="label" 
                    variant="body2" 
                    sx={{ 
                      mb: 1,
                      display: 'block',
                      color: 'var(--color-blue) !important',
                      fontWeight: 500,
                      fontSize: '14px',
                    }}
                  >
                    Email address
                  </Typography>
                  <TextField
                    fullWidth
                    id="email"
                    name="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    variant="outlined"
                    sx={{ 
                      width: '100%',
                      height: '45px',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '4px',
                        color: 'var(--color-blue) !important',
                        height: '45px',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#475969',
                          borderWidth: '1px',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#475969',
                          borderWidth: '1px',
                          borderRadius: '4px',
                        },
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4759694d',
                        border: '1px solid #4759694d',
                        borderRadius: '4px',
                      },
                      '& .MuiInputBase-input': {
                        padding: '12px 14px',
                        fontSize: '14px',
                        color: 'var(--color-blue) !important',
                      },
                      '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
                        WebkitBoxShadow: '0 0 0 1000px white inset',
                        transition: 'background-color 5000s ease-in-out 0s',
                        WebkitTextFillColor: 'var(--color-blue)',
                      },
                    }}
                  />
                </Box>
                
                {/* Submit Button */}
                <Box sx={{ width: '304px', mb: 2 }}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    sx={{ 
                      py: 1.2,
                      height: '40px',
                      bgcolor: '#485869', 
                      color: '#ffffff',
                      fontFamily: '"Sofia Pro Regular", "Sofia Pro", sans-serif',
                      fontSize: 'var(--font-size-20px)',
                      lineHeight: 'var(--line-height-30px)',
                      fontWeight: 400,
                      border: '1px solid #485869',
                      borderRadius: '5px',
                      textTransform: 'none !important',
                      '&:hover': {
                        bgcolor: '#3A4754',
                      },
                      boxShadow: 'none',
                      mb: 2,
                    }}
                  >
                    {loading ? <CircularProgress size={20} color="inherit" /> : 'Send reset instructions'}
                  </Button>
                </Box>
                
                {/* Back to Login Link */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                  <Typography 
                    variant="body2" 
                    component="a"
                    onClick={() => navigate('/login')}
                    sx={{ 
                      cursor: 'pointer', 
                      color: 'var(--color-blue) !important',
                      textDecoration: 'none',
                      fontWeight: 500,
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Back to login
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default ForgotPassword;